import Papa, { type ParseResult } from 'papaparse'
import { LOGO_BRAND_NAME } from '@/lib/seatingPlannerLogo'
import type { PlanState, SeatingGuest } from '@/types/seatingPlanner'

const NAME_HEADERS = ['name', 'full name', 'guest', 'guest name', 'attendee']
const FIRST_NAME_HEADERS = ['first name', 'firstname', 'first']
const LAST_NAME_HEADERS = ['last name', 'lastname', 'last', 'surname']
const NOTES_HEADERS = ['notes', 'note', 'comment', 'comments']

const normalizeHeader = (h: string) => h.trim().toLowerCase()

const findKey = (row: Record<string, string>, candidates: string[]): string | undefined => {
  for (const key of Object.keys(row)) {
    if (candidates.includes(normalizeHeader(key))) return key
  }
  return undefined
}

export type CsvImportResult = {
  guests: SeatingGuest[]
  skipped: number
  total: number
}

export function parseGuestsCsv(file: File, existingGuests: SeatingGuest[] = []): Promise<CsvImportResult> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, string>>) => {
        try {
          resolve(buildGuests(results.data, existingGuests))
        } catch (err) {
          reject(err instanceof Error ? err : new Error('csv_parse_failed'))
        }
      },
      error: (err: unknown) => reject(err instanceof Error ? err : new Error(String(err))),
    })
  })
}

function buildGuests(rows: Record<string, string>[], existing: SeatingGuest[]): CsvImportResult {
  const total = rows.length
  const dedupeKeys = new Set(existing.map((g) => (g.name || '').toLowerCase()))

  const guests: SeatingGuest[] = []
  let skipped = 0

  for (const row of rows) {
    const nameKey = findKey(row, NAME_HEADERS)
    const firstKey = findKey(row, FIRST_NAME_HEADERS)
    const lastKey = findKey(row, LAST_NAME_HEADERS)
    const notesKey = findKey(row, NOTES_HEADERS)

    let name = nameKey ? (row[nameKey] || '').trim() : ''
    if (!name) {
      const first = firstKey ? (row[firstKey] || '').trim() : ''
      const last = lastKey ? (row[lastKey] || '').trim() : ''
      name = [first, last].filter(Boolean).join(' ').trim()
    }
    const notes = notesKey ? (row[notesKey] || '').trim() : ''

    if (!name) {
      skipped += 1
      continue
    }

    const key = name.toLowerCase()
    if (dedupeKeys.has(key)) {
      skipped += 1
      continue
    }
    dedupeKeys.add(key)

    guests.push({
      id: cryptoRandomId(),
      name,
      ...(notes ? { notes } : {}),
    })
  }

  return { guests, skipped, total }
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `g_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
}

export function exportPlanToCsv(state: PlanState, filename = 'seating-chart.csv'): void {
  const tablesById = new Map(state.tables.map((t) => [t.id, t]))

  const rows = state.guests.map((g) => {
    const assignment = state.assignments[g.id]
    const table = assignment ? tablesById.get(assignment.tableId) : undefined
    return {
      'Guest Name': g.name,
      'Table Number': table?.number ?? '',
      'Table Name': table?.label ?? '',
      Seat: assignment ? String(assignment.seatIndex + 1) : '',
      Notes: g.notes ?? '',
    }
  })

  rows.sort((a, b) => {
    const cmp = naturalCompare(a['Table Number'], b['Table Number'])
    if (cmp !== 0) {
      // Empty table numbers (unassigned) sort last.
      if (!a['Table Number']) return 1
      if (!b['Table Number']) return -1
      return cmp
    }
    const seatCmp = naturalCompare(a.Seat, b.Seat)
    if (seatCmp !== 0) return seatCmp
    return a['Guest Name'].localeCompare(b['Guest Name'])
  })

  const header = buildCsvHeader(state)
  const csv = `${header}${Papa.unparse(rows)}`
  triggerDownload(csv, filename)
}

function buildCsvHeader(state: PlanState): string {
  const event = state.event ?? { name: '', date: '', venue: '' }
  const lines: string[][] = [[LOGO_BRAND_NAME]]
  if (event.name?.trim()) lines.push(['Event', event.name.trim()])
  if (event.date?.trim()) lines.push(['Date', event.date.trim()])
  if (event.venue?.trim()) lines.push(['Venue', event.venue.trim()])
  // Blank separator row before the data table.
  lines.push([])
  return `${Papa.unparse(lines)}\n`
}

function triggerDownload(csv: string, filename: string): void {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}
